import { useMemo } from "react";
import type { TimeReportsV2Group } from "services/api/company/timeReports.service";
import { GroupDivider } from "web-apps/company/components/common/shared/groupDivider.component";
import type { TimeReportsV2DisplayOption } from "web-apps/company/stores/timeReportsV2/timeReportsV2Store.reducer";
import type { TimeReportsGroupByOption } from "web-apps/company/components/timeReportsV2/components/toolbar/timeReportsToolbar.component";
import type { PersonDetailSelection } from "web-apps/company/components/timeReportsV2/components/views/person/models/personView.types";
import type { InlineTimeReportEditActivity } from "web-apps/company/components/timeReportsV2/components/views/person/hooks/useInlineTimeReportEdit";
import { PersonRow } from "./row/personRow.component";

interface PersonRowsListProps {
  readonly companyId?: number;
  readonly deviationToleranceMinutes: number;
  readonly displayOption: TimeReportsV2DisplayOption;
  readonly groupByOption: TimeReportsGroupByOption;
  readonly includeFlexTimeDeviations: boolean;
  readonly isAllRowsExpanded: boolean;
  readonly isDetailOpeningDisabled?: boolean;
  readonly isReadOnly: boolean;
  readonly queryScopeKey: string;
  readonly rows: TimeReportsV2Group[];
  readonly selectedDetailIdSet: ReadonlySet<number>;
  readonly onDetailClick: (selection: PersonDetailSelection) => void;
  readonly onDownloadRowCsv: (row: TimeReportsV2Group) => void;
  readonly onInlineEditPendingChange?: (
    activity: InlineTimeReportEditActivity,
  ) => void;
  readonly onInlineEditSaved: () => void;
  readonly onSelectionChange: (
    selection: PersonDetailSelection[],
    checked: boolean,
  ) => void;
  readonly translate: (key: string) => string;
}

interface PersonRowWithDivider {
  readonly divider: JSX.Element | null;
  readonly key: string;
  readonly row: TimeReportsV2Group;
}

export const PersonRowsList = (props: PersonRowsListProps): JSX.Element => {
  const rows = props.rows;
  const groupByOption = props.groupByOption;
  const companyId = props.companyId;
  const queryScopeKey = props.queryScopeKey;
  const selectedDetailIdSet = props.selectedDetailIdSet;
  const translate = props.translate;

  const rowsWithDividers = useMemo<PersonRowWithDivider[]>(() => {
    if (groupByOption !== "team") {
      return rows.map((row) => ({
        key: `${companyId}|${queryScopeKey}|${row.id}`,
        row,
        divider: null,
      }));
    }

    const result: PersonRowWithDivider[] = [];
    let lastWorkGroupId: number | null = null;

    rows.forEach((row) => {
      const firstItem = row.items[0];

      const workGroupId = firstItem?.workGroupId ?? null;

      const rawUnitName = (firstItem?.companyUnitName ?? "").trim();

      const unitName =
        rawUnitName.length > 0
          ? rawUnitName
          : translate("Schedule.UnknownUnit");

      const rawTeamName = (firstItem?.workGroupName ?? "").trim();

      const teamName =
        rawTeamName.length > 0
          ? rawTeamName
          : translate("Schedule.UnknownTeam");

      const shouldRenderDivider =
        workGroupId !== null && workGroupId !== lastWorkGroupId;

      result.push({
        key: `${companyId}|${queryScopeKey}|${workGroupId}|${row.id}`,
        row,
        divider: shouldRenderDivider ? (
          <GroupDivider
            key={`divider-${String(workGroupId)}`}
            unitName={unitName}
            teamName={teamName}
          />
        ) : null,
      });
      lastWorkGroupId = workGroupId;
    });

    return result;
  }, [companyId, groupByOption, queryScopeKey, rows, translate]);

  return (
    <>
      {rowsWithDividers.map((entry) => (
        <div key={entry.key}>
          {entry.divider}
          <PersonRow
            row={entry.row}
            displayOption={props.displayOption}
            companyId={props.companyId}
            isReadOnly={props.isReadOnly}
            onInlineEditSaved={props.onInlineEditSaved}
            onInlineEditPendingChange={props.onInlineEditPendingChange}
            isDetailOpeningDisabled={props.isDetailOpeningDisabled}
            isExpandedByDefault={props.isAllRowsExpanded}
            deviationToleranceMinutes={props.deviationToleranceMinutes}
            includeFlexTimeDeviations={props.includeFlexTimeDeviations}
            onDownloadClick={props.onDownloadRowCsv}
            selectedDetailIds={entry.row.items
              .filter((item) => selectedDetailIdSet.has(item.id))
              .map((item) => item.id)}
            onSelectionChange={props.onSelectionChange}
            onDetailClick={props.onDetailClick}
          />
        </div>
      ))}
    </>
  );
};
